import ToastificationContent from "@core/components/toastification/ToastificationContent";
import {ProductProvider, clearCachedProduct} from "@core/domain/product";
import {i18n} from "@/libs/i18n";
import {getAllSubstanceLists} from "@core/domain/substance";
import backendResponse from "@core/mixins/ui/backend-response";
import {ProductType} from "@/libs/product";
import {LegislationApi} from "@core/api";
import {LegislationKey} from "@core/domain/legislation";
import {DirtyStateTracker} from "@core/domain/dirty-state";

const ProductStateSection = Object.freeze({
    CARD: 'card',
    LEGISLATION_QUESTION_ANSWERS: 'legislationQuestionAnswers',
    SUBSTANCES: 'substances',
    FMD: 'fmd',
    DOCUMENTS: 'documents',
    SUPPLIER_DOCUMENTS: 'supplierDocuments',
});

export default {
    data: {
        dirtyStateTracker: null,
        substanceLists: [],
        product: {
            type: '',
            names: [],
            identifiers: [],
            businessRoles: [],
            comment: '',
            productGroups: [],
            includedProducts: [],
            eventStatuses: [],
            categories: [],
            hasConcernSubstances: null,
            countryOfOrigin: null,
            canDelete: false,
            informationOwnerSettings: {
                weHaveInformation: null,
                supplierHasInformation: null
            },
            legislations: {},
            hasBeenScipNotified: null,
            submissionDetails: null,
            isPutOnTheMarket: null,
            isTopLevel: null,
            regulatedProductStatements: [],
            legislationQuestionAnswers: [],
        },
        scipNotificationFacts: {
            isNotificationGranted: false,
            isSubscriberProperlyConfigured: false,
            productDecision: {
                canBeNotified: false,
                hasChangesToBeReported: false,
                hasSubstancesAboveSpecifiedConcentrationRange: false,
                hasScipNumbersOnComponentLevel: false,
                hasScipNumberReference: false,
            },
            sandBoxSettings: null
        },
        productDescription: {file: null},
        includedSubstances: [],
        excludedSubstances: [],
        evidenceReports: [],
        otherVerificationDocuments: [],
        declarationsOfConformity: [],
        compliantStatement: [],
        safeUseInstructions: {
            description: '',
            file: null
        },
        disassemblingInstructions: {
            description: '',
            file: null
        },
        safeUseApplicable: null,
        needToSaveSubstances: false,

        suppliers: [],
        legislations: [],

        fmdDeclarations: [],
        loadingFMDDeclarations: false,

        isScipEnabled: true,
        isRohsEnabled: true,
        statusExplanationRevision: 1
    },
    methods: {
        reInitData() {
            this.dirtyStateTracker = new DirtyStateTracker();
            if (typeof this.initProductStatementAnswers === 'function') {
                this.productStatementAnswerRows = [];
            }
            if (typeof this.resetLegislationQuestionAnswersState === 'function') {
                this.resetLegislationQuestionAnswersState();
            }
            this.substanceList = null;
            this.product = {
                type: '',
                names: [],
                identifiers: [],
                businessRoles: [],
                comment: '',
                productGroups: [],
                includedProducts: [],
                eventStatuses: [],
                categories: [],
                characteristics: {},
                countryOfOrigin: null,
                hasConcernSubstances: null,
                canDelete: false,
                informationOwnerSettings: {
                    weHaveInformation: null,
                    supplierHasInformation: null
                },
                isPutOnTheMarket: null,
                substanceListQuestionStates: [],
                regulatedProductStatements: [],
                legislationQuestionAnswers: [],
            };
            this.scipNotificationFacts = {
                isNotificationGranted: false,
                isSubscriberProperlyConfigured: false,
                productDecision: {
                    hasChangesToBeReported: false,
                    hasSubstancesAboveSpecifiedConcentrationRange: false,
                    hasScipNumbersOnComponentLevel: false
                },
                sandBoxSettings: null
            };
            this.fmdDeclarations = [];
            this.includedSubstances = [];
            this.excludedSubstances = [];
            this.evidenceReports = [];
            this.otherVerificationDocuments = [];
            this.declarationsOfConformity = [];
            this.compliantStatement = [];
            this.safeUseInstructions = {
                description: '',
                file: null
            };
            this.disassemblingInstructions = {
                description: '',
                file: null
            };
            this.safeUseApplicable = null;
            this.needToSaveSubstances = false;
            this.suppliers = [];
            this.isScipEnabled = true;
            this.isRohsEnabled = true;
        },
        getProductId() {
            throw new Error('getProductId method has to be implemented by the client');
        },
        informationOwnerSettingsChanged(settings) {
            const product = this.getProduct();

            if (product.isInited()) {
                product.setInformationOwnerSettings(settings);
                return;
            }

            product
                .getCard()
                .then(() => {
                    product.setInformationOwnerSettings(settings);
                });
        },
        updateLegislationSettings({key, settings}) {
            const product = this.getProduct();
            product
                .getCard()
                .then(() => {
                    product.setLegislationSettings(key, settings);
                });
        },
        suppliersChange(idList, suppliers) {
            this.suppliers = suppliers;
            const product = this.getProduct();
            product
                .getCard()
                .then(() => {
                    product.setSuppliers(suppliers);
                });
        },
        onFMDChange(change) {
            this.fmdDeclarations = change.declarations;
            this.getProduct().setFMDDeclarations(change.declarations);
        },
        onLegislationQuestionAnswersChange(answers) {
            if (Array.isArray(this.legislationQuestionAnswers)) {
                this.legislationQuestionAnswers = answers;
            }
            this.getProduct().setLegislationQuestionAnswers(answers);
        },
        setSuppliers(suppliers) {
            this.suppliers = suppliers;
        },
        getProduct() {
            return ProductProvider(this.getProductId());
        },
        isCurrentProduct(productId) {
            return String(this.getProductId()) === String(productId);
        },
        refreshProduct() {
            const productId = this.getProductId();

            return ProductProvider(productId)
                .refreshCard()
                .then(card => {
                    if (!this.isCurrentProduct(productId)) {
                        return card;
                    }

                    this.initProduct(card);
                    if (typeof this.fetchLegislationQuestionAnswers === 'function') {
                        return this.fetchLegislationQuestionAnswers(true);
                    }

                    return card;
                })
            ;
        },
        init() {
            this.reInitData();
            getAllSubstanceLists().then(lists => this.substanceLists = lists);
            const legislationApiPromise = LegislationApi.all().then(r => {
                this.legislations = r.data;

                if (!this.legislations.find(l => l.key === LegislationKey.SCIP)) {
                    this.isScipEnabled = false;
                }
                if (!this.legislations.find(l => l.key === LegislationKey.ROHS)) {
                    this.isRohsEnabled = false;
                }
            });

            const product = this.getProduct();
            const productId = this.getProductId();
            return product
                .getCard()
                .then((card) => {
                    if (!this.isCurrentProduct(productId)) {
                        return false;
                    }

                    this.initProduct(card);
                    if (typeof this.fetchLegislationQuestionAnswers === 'function') {
                        return this.fetchLegislationQuestionAnswers()
                            .then(() => this.isCurrentProduct(productId));
                    }

                    return true;
                })
                .then(initialized => {
                    if (!initialized || !this.isCurrentProduct(productId)) {
                        return;
                    }

                    setTimeout(() => this.showLoading = false, 400);
                    setTimeout(() => {
                        if (!this.isCurrentProduct(productId)) {
                            return;
                        }

                        legislationApiPromise.then(() => {
                            if (this.isCurrentProduct(productId)) {
                                this.initScipNotificationFacts(product);
                            }
                        });
                        this.fetchSubstancesAndDocuments();
                    }, 600);
                })
        },

        initScipNotificationFacts(product) {
            if (this.isScipEnabled) {
                product
                    .getSCIPNotificationFacts()
                    .then(facts => {
                        this.scipNotificationFacts = facts;
                    });
            }
        },

        initProduct(product) {
            this.product = product;
            if (typeof this.initProductStatementAnswers === 'function') {
                this.initProductStatementAnswers();
            }

            this.safeUseApplicable = product.isSafeUseApplicable;
            if (this.product.suppliers) {
                this.suppliers = this.product.suppliers;
            }

            const domainProduct = this.getProduct();
            this.markProductStateSectionReady(
                ProductStateSection.CARD,
                () => domainProduct.getCardState(),
            );
            this.initProductBreadcrumbs();
        },

        initProductBreadcrumbs() {},

        markProductStateSectionReady(section, getCurrentState) {
            if (this.dirtyStateTracker) {
                this.dirtyStateTracker.markReady(section, getCurrentState);
            }
        },

        markProductStateSectionClean(section) {
            if (this.dirtyStateTracker && this.dirtyStateTracker.isReady(section)) {
                this.dirtyStateTracker.markClean(section);
            }
        },

        isProductStateSectionChanged(section) {
            return this.dirtyStateTracker
                && this.dirtyStateTracker.isReady(section)
                && this.dirtyStateTracker.isChanged(section);
        },

        markLegislationQuestionAnswersStateReady(productId) {
            if (String(this.getProductId()) !== String(productId)) {
                return;
            }

            const product = ProductProvider(productId);
            this.markProductStateSectionReady(
                ProductStateSection.LEGISLATION_QUESTION_ANSWERS,
                () => product.getLegislationQuestionAnswersState(),
            );
        },

        onSupplierVerificationDocumentsReady(productId = this.getProductId()) {
            if (!this.isCurrentProduct(productId)) {
                return;
            }

            const product = ProductProvider(productId);
            this.markProductStateSectionReady(
                ProductStateSection.SUPPLIER_DOCUMENTS,
                () => product.getSupplierVerificationDocumentsState(),
            );
        },

        hasUnSavedChanges() {
            return this.dirtyStateTracker ? this.dirtyStateTracker.hasChanges() : false;
        },

        changedProductStateSections() {
            return this.dirtyStateTracker ? this.dirtyStateTracker.changedSections() : [];
        },

        deleteDocument(document) {
            return new Promise((resolve, reject) => {
                this.$swal({
                    title: i18n.t('Component(DeleteDocumentPrompt):title'),
                    text: i18n.t('Component(DeleteDocumentPrompt):text'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: i18n.t('Component(DeleteDocumentPrompt):button-delete'),
                    cancelButtonText: i18n.t('Component(Button):cancel'),
                    customClass: {
                        confirmButton: 'btn btn-danger',
                        cancelButton: 'btn btn-outline-secondary ml-1',
                    },
                    buttonsStyling: false,
                }).then(result => {
                    if (result.isConfirmed) {
                        ProductProvider(this.getProductId())
                            .deleteDocument(document)
                            .then(() => {
                                resolve();
                                this.$swal({
                                    icon: 'success',
                                    title: i18n.t('Component(DeleteDocumentAck):title'),
                                    text: i18n.t('Component(DeleteDocumentAck):text'),
                                    customClass: {
                                        confirmButton: 'btn btn-primary',
                                    },
                                })
                            })
                            .catch((error) => {
                                let errorMessage = 'Some error occurred, please contact your administrator';
                                if (error.response) {
                                    errorMessage = error.response.data.title || errorMessage;
                                } else if (error.request) {
                                    // The request was made but no response was received
                                    console.log(error.request);
                                } else {
                                    errorMessage = error.message;
                                }
                                this.$swal({
                                    icon: 'error',
                                    title: 'Error!',
                                    text: errorMessage,
                                    customClass: {
                                        confirmButton: 'btn btn-primary',
                                    },
                                    showClass: {
                                        popup: 'animate__animated animate__shakeX',
                                    },
                                    buttonsStyling: false,
                                })
                                reject(e);
                            })
                    } else {
                        reject();
                    }
                })
            })
        },

        safeUseApplicableChange(isApplicable) {
            ProductProvider(this.getProductId()).isSafeUseApplicable = isApplicable;
            this.safeUseApplicable = isApplicable;
        },

        addDoC(document) {
            this.declarationsOfConformity.push(document);
        },

        /** @deprecated Use section readiness or clean boundaries instead. */
        updateProductState() {
            if (this.dirtyStateTracker) {
                this.dirtyStateTracker.markAllClean();
            }
        },

        removeDoC(document, index) {
            return new Promise((resolve, reject) => {
                if (document.id) {
                    const canRebaseDocuments = !this.isProductStateSectionChanged(ProductStateSection.DOCUMENTS);
                    this.deleteDocument(document).then(() => {
                        this.declarationsOfConformity.splice(index, 1);
                        if (canRebaseDocuments) {
                            this.markProductStateSectionClean(ProductStateSection.DOCUMENTS);
                        }
                        resolve();
                    }).catch(reject);
                } else {
                    this.declarationsOfConformity.splice(index, 1);
                    resolve();
                }
            });
        },

        addCompliantStatement(document) {
            this.compliantStatement.push(document);
        },

        removeCompliantStatement(document, index) {
            return new Promise((resolve, reject) => {
                if (document.id) {
                    const canRebaseDocuments = !this.isProductStateSectionChanged(ProductStateSection.DOCUMENTS);
                    this.deleteDocument(document).then(() => {
                        this.compliantStatement.splice(index, 1);
                        if (canRebaseDocuments) {
                            this.markProductStateSectionClean(ProductStateSection.DOCUMENTS);
                        }
                        resolve();
                    }).catch(reject);
                } else {
                    this.compliantStatement.splice(index, 1);
                    resolve();
                }
            });

        },

        addReport(report) {
            this.evidenceReports.push(report);
        },

        removeReport(report, index) {
            return new Promise((resolve, reject) => {
                if (report.id) {
                    const canRebaseDocuments = !this.isProductStateSectionChanged(ProductStateSection.DOCUMENTS);
                    this.deleteDocument(report).then(() => {
                        this.evidenceReports.splice(index, 1);
                        if (canRebaseDocuments) {
                            this.markProductStateSectionClean(ProductStateSection.DOCUMENTS);
                        }
                        resolve();
                    }).catch(reject);
                } else {
                    this.evidenceReports.splice(index, 1);
                    resolve();
                }
            });
        },

        onWeightChange(weight) {
            this.productCharacteristicsChange({weight: parseFloat(weight)});
        },

        productCharacteristicsChange(characteristics) {
            ProductProvider(this.getProductId()).setProductCharacteristics(characteristics);
        },

        productDescriptionChange(descriptionFile) {
            ProductProvider(this.getProductId()).setProductDescription(descriptionFile);
        },

        commentChange(comment) {
            ProductProvider(this.getProductId()).setComment(comment);
        },

        safeUseChange(instructions) {
            this.safeUseInstructions = instructions;
            ProductProvider(this.getProductId()).setSafeUseDocument(instructions);
        },

        disassemblingChange(instructions) {
            this.disassemblingInstructions = instructions;
            ProductProvider(this.getProductId()).setDissemblingDocument(instructions);
        },
        onSubstancePropertiesChange(substanceContainer, properties) {
            substanceContainer.properties = properties;
            this.needToSaveSubstances = true;
        },

        /**
         * @deprecated
         * @param substance
         * @param details
         */
        onSubstanceDetailsChange(substance, details) {
            this.needToSaveSubstances = true;
            // substance.configuration = details;
            // substance.details = details;
        },

        listQuestionStateUpdated(list, state) {
            ProductProvider(this.getProductId()).updateListQuestionState(list, state);
        },

        includeConcernSubstance(substance, substanceListIds = null) {
            this.needToSaveSubstances = true;
            const listIds = Array.isArray(substanceListIds) && substanceListIds.length > 0
                ? substanceListIds
                : this.legislations.map(l => l.substanceList.id);

            ProductProvider(this.getProductId()).includeSubstance(
                substance,
                listIds
            );
        },

        excludeConcernSubstance(substance) {
            this.needToSaveSubstances = true;
            ProductProvider(this.getProductId()).excludeSubstance(substance);
        },

        makeSubstanceUndefined(substance) {
            this.needToSaveSubstances = true;
            ProductProvider(this.getProductId()).makeSubstanceUndefined(substance);
        },

        hasConcernSubstances(value) {
            this.product.hasConcernSubstances = value;
        },

        //  Fetch

        fetchSubstancesAndDocuments() {
            return Promise
                .all([
                    this.isFMDTabVisible ? this.fetchFMDDeclarations(true) : true,
                    this.fetchSubstances(),
                    this.fetchDocuments()
                ]);
        },

        fetchFMDDeclarations(refresh = false) {
            const productId = this.getProductId();
            const product = ProductProvider(productId);
            this.loadingFMDDeclarations = true;
            return product
                .getFMDDeclarations(refresh)
                .then(declarations => {
                    if (!this.isCurrentProduct(productId)) {
                        return;
                    }

                    this.fmdDeclarations = declarations;
                    this.markProductStateSectionReady(
                        ProductStateSection.FMD,
                        () => product.getFMDState(),
                    );
                })
                .finally(() => {
                    if (this.isCurrentProduct(productId)) {
                        this.loadingFMDDeclarations = false;
                    }
                });
        },

        fetchSubstances(refresh = false) {
            const productId = this.getProductId();
            const product = ProductProvider(productId);

            return product
                .getSubstances(refresh)
                .then(({included, excluded}) => {
                    if (!this.isCurrentProduct(productId)) {
                        return;
                    }

                    this.includedSubstances = included;
                    this.excludedSubstances = excluded;
                    this.markProductStateSectionReady(
                        ProductStateSection.SUBSTANCES,
                        () => product.getSubstancesState(),
                    );
                });
        },

        fetchDocuments(refresh = false) {
            const productId = this.getProductId();
            const product = ProductProvider(productId);

            return product
                .getDocuments(refresh)
                .then((documents) => {
                    if (!this.isCurrentProduct(productId)) {
                        return;
                    }

                    this.declarationsOfConformity = documents.declarationOfConformityDocuments;
                    this.compliantStatement = documents.compliantStatementDocuments;
                    this.evidenceReports = documents.evidenceReportDocuments;
                    this.otherVerificationDocuments = documents.otherVerificationDocuments;
                    this.safeUseInstructions = documents.safeUseDocument;
                    this.disassemblingInstructions = documents.dissemblingDocument;
                    this.productDescription = documents.productDescription;
                    this.markProductStateSectionReady(
                        ProductStateSection.DOCUMENTS,
                        () => product.getDocumentsState(),
                    );
                });
        },

        //  Save
        save(showSuccessMessage = true, showToast = false, showSuccessToast = false, manageLoading = true) {
            if (manageLoading) {
                this.showLoading = true;
            }

            if (showToast) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Notification',
                        icon: 'BellIcon',
                        text: 'Saving changes',
                        variant: 'info'
                    },
                });
            }

            const productId = this.getProductId();
            const product = ProductProvider(productId);
            return product
                .save()
                .then(() => {
                    if (!this.isCurrentProduct(productId)) {
                        return;
                    }

                    if (showSuccessMessage) {
                        this.$swal({
                            title: i18n.t('Component(ProductSavedAck):title'),
                            text: i18n.t('Component(ProductSavedAck):text'),
                            icon: 'success',
                            timer: 1300,
                            customClass: {
                                confirmButton: 'btn btn-primary',
                            },
                            buttonsStyling: false,
                        });
                    } else if (showSuccessToast) {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: 'Notification',
                                icon: 'BellIcon',
                                text: 'Product has been successfully saved!',
                                variant: 'success'
                            },
                        });
                    }
                    this.statusExplanationRevision += 1;
                    return Promise.all([
                        this.refreshProduct(),
                        this.fetchSubstancesAndDocuments(),
                    ]).then(() => {
                        if (this.isCurrentProduct(productId) && this.dirtyStateTracker) {
                            this.dirtyStateTracker.markAllClean();
                        }
                        return true;
                    });
                })
                .catch((e) => {
                    backendResponse.methods.handleBackendError.call(this, e);
                    if (e.productStatementAnswersRejected && this.isCurrentProduct(productId)
                        && typeof this.refreshRejectedProductStatementAnswers === 'function') {
                        return this.refreshRejectedProductStatementAnswers(productId)
                            .catch(error => backendResponse.methods.handleBackendError.call(this, error))
                            .then(() => false);
                    }
                    return false;
                })
                .finally(() => {
                    if (manageLoading) {
                        this.showLoading = false;
                    }
                });
        },

        remove() {
            return this.getProduct().remove().then(r => {
                clearCachedProduct(this.getProductId())
            });
        },
    },

    computed: {
        // Tabs
        isConcernElementsTabAccessable() {
            return false === this.isComplexProduct;
        },

        isComplexProduct() {
            return this.product.isComplex;
        },

        isProductHasConcernElements() {
            return this.includedSubstances.length > 0;
        },

        isVerificationTabVisible() {
            if (this.product.type === ProductType.FICTITIOUS) {
                return false;
            }
            return true;
            // return this.product.type !== ProductType.PURCHASED || this.product.isPutOnTheMarket !== false;
        },

        isFMDTabVisible() {
            if (!this.$store.getters.isFullMaterialDeclarationEnabled) {
                return false;
            }
            return this.product.type !== ProductType.FICTITIOUS;
        },

        isCompositionTabVisible() {
            if (this.isFMDTabVisible) {
                return false;
            }
            return this.$can('view', 'tab:product-card:composition') && this.product.type !== ProductType.FICTITIOUS;
        },

        isConcernElementsTabVisible() {
            if (this.$store.getters.isFullMaterialDeclarationEnabled) {
                return false;
            }
            return this.$can('view', 'tab:product-card:concern-elements') && this.product.type !== ProductType.FICTITIOUS;
        },

        isSupplierDocumentsTabVisible() {
            // if (this.product.type !== ProductType.PURCHASED) {
            //     return false;
            // }
            // return this.product.businessRoles.length === 0 || this.product.isPutOnTheMarket === false;
            return true;
        },
        isStatisticsTabVisible() {
            if (!this.$store.getters.organization.settings.statisticSettings) {
                return false;
            }
            if (!this.$store.getters.organization.settings.statisticSettings.showProductStatistics) {
                return false;
            }
            return this.product.type === ProductType.OWN && this.product.isTopLevel === true;
        },
        applicableLegislations() {
            const productLegislations = Array.isArray(this.product.legislations)
                ? this.product.legislations
                : Object.values(this.product.legislations || {})

            const applicableById = {};
            productLegislations.forEach((legislation) => {
                if (!legislation || legislation.id === undefined || legislation.id === null) {
                    return;
                }
                applicableById[String(legislation.id)] = legislation.isApplicable !== false;
            });

            return this.legislations.filter((legislation) => {
                if (!legislation || legislation.id === undefined || legislation.id === null) {
                    return false;
                }

                const id = String(legislation.id);
                if (!Object.prototype.hasOwnProperty.call(applicableById, id)) {
                    return true;
                }

                return applicableById[id];
            });
        }
    }
};
